import {useEffect} from 'react';
import {useTranslation} from 'react-i18next';
import {useDispatch} from 'react-redux';
import Anchor from '../../../common/component/Anchor';
import EllipsisText from '../../../common/component/EllipsisText';
import Modal, {
  FULL_CONTENT_STYLE,
  FULL_OVERLAY_STYLE,
} from '../../../common/component/Modal';
import PageImage from '../../../common/component/PageImage';
import SingleText from '../../../common/component/SingleText';
import Text from '../../../common/component/Text';
import TextButton from '../../../common/component/TextButton';
import {REGION} from '../../../common/constant';
import useLocalStorageArray from '../../../common/hook/useLocalStorageArray';
import useScreenType from '../../../common/hook/useScreenType';
import useTypedSelector from '../../../common/hook/useTypedSelector';
import {routeTo} from '../../../common/util/PageRouter';
import useDelayClosePopup from '../../../commonSub/hook/useDelayClosePopup';
import {actions} from '../state/action';
import {sendHomeEvent, sendHomeViewImp} from '../tracking';
import {IMessageProps} from '../type';

export default function EventPopup({onFinish}: IMessageProps) {
  const dispatch = useDispatch();
  const {t} = useTranslation();
  const {isSmall} = useScreenType();
  const [neverShowEventPopupIds, pushNeverShowEventPopupIds] =
    useLocalStorageArray<number>({
      key: 'never-show-event-popup-ids',
      isUnique: true,
    });

  const eventPopupList = useTypedSelector(state => state.home.eventPopupList);
  const eventPopup = eventPopupList?.find(
    ({id}) => !neverShowEventPopupIds.includes(id)
  );

  useDelayClosePopup({isShowPopup: !!eventPopup, onFinish});

  useEffect(() => {
    if (eventPopup) {
      sendHomeViewImp({
        page: 'main',
        customProps: {
          popup_id: eventPopup.id,
          event_layer: 'popup',
          popup_type: 'front',
        },
        viewImpContents: {name: 'popup'},
      });
    }
  }, [eventPopup]);

  const onRequestClose = () => {
    sendHomeEvent({
      actionName: 'popup_close',
      action: 'click',
      page: 'main',
      customProps: {
        popup_type: 'front',
        event_layer: 'popup',
        popup_id: eventPopup?.id,
      },
    });
    dispatch(actions.setValue('eventPopupList', undefined));
  };

  const onClose = (isNotAgain?: boolean) => {
    onRequestClose();
    eventPopup?.id && isNotAgain && pushNeverShowEventPopupIds(eventPopup.id);
  };
  const isKorea = REGION === 'kor';

  return (
    <Modal
      overlayStyle={{
        ...FULL_OVERLAY_STYLE,
        backgroundColor: 'rgba(var(--any-black), 0.85)',
      }}
      contentStyle={{
        ...FULL_CONTENT_STYLE,
        backgroundColor: 'transparent',
        height: isSmall ? '483px' : '503px',
        top: 'auto',
        left: 0,
        bottom: 0,
      }}
      isOpen={!!eventPopup}
      onRequestClose={onRequestClose}
      className={{
        base: 'AlertModal__Content bg-grey-01 light:bg-background',
        afterOpen: 'AlertModal__Content--after-open',
        beforeClose: 'AlertModal__Content--before-close',
      }}
    >
      {eventPopup && (
        <div className={cn('absolute text-center w-full h-full')}>
          <div className="absolute bg-grey-01 light:bg-white bottom-0 h-[176px] sm:h-[183px] z-1 w-full" />
          <div className="absolute bottom-[176px] sm:bottom-[183px] inset-x-0 m-auto">
            <PageImage
              className="w-[300px] h-[300px] mb-38 sm:w-[320px] sm:h-[320px] sm:mb-39"
              src={eventPopup.popupImgUrl}
              alt=""
            />
            <div
              className={cn(
                'absolute z-2 mt-80 inset-x-0',
                isKorea && '-bottom-25 sm:-bottom-32',
                !isKorea && '-bottom-29'
              )}
            >
              <EllipsisText
                line={3}
                className={cn(
                  'max-w-[272px] leading-26 m-auto light:text-black',
                  isKorea &&
                    's18-bold-white sm:text-[22px] sm:leading-29 sm:tracking-[-0.5px]',
                  !isKorea && 's19-extrabold-white sm:tracking-[-0.4px]'
                )}
              >
                {eventPopup.title}
              </EllipsisText>
              <SingleText
                className={cn(
                  'm-auto',
                  'sm:leading-21',
                  isKorea &&
                    's14-regular-grey13 sm:text-[15px] mt-8 max-w-[282px]',
                  !isKorea && 's12-regular-grey13 mt-11 max-w-[272px]'
                )}
              >
                {eventPopup.date}
              </SingleText>
            </div>
            <div
              className={cn(
                'absolute sm:bottom-54 bottom-48 h-100 z-1 w-full',
                'bg-gradient-to-b from-[rgba(var(--grey-01),0)] to-grey-01 light:from-[rgba(var(--background),0)] light:to-background'
              )}
            />
            <div
              className={cn(
                'absolute z-1',
                'sm:-bottom-26 -bottom-32 h-80 w-full',
                'bg-grey-01 light:bg-white'
              )}
            />
          </div>
          <div className="h-[334px] sm:h-[347px] rounded-t-18 bg-grey-01 light:bg-white absolute inset-x-0 -z-1 bottom-0" />
          <div className="fixed bottom-67 flex justify-between inset-x-20 max-w-[630px] z-1 m-auto">
            <TextButton
              className={cn(
                'h-50 overflow-hidden w-[calc(50%-7.5px)] btn-transparent-grey-06',
                's16-bold-grey13'
              )}
              onClick={() => {
                onClose();
              }}
              isActiveTextOpacity50
            >
              {t('common_close')}
            </TextButton>
            <TextButton
              className={cn(
                'px-20 h-50 overflow-hidden w-[calc(50%-7.5px)]',
                'btn-white light:btn-black'
              )}
              textClassName="s16-bold-black light:text-white"
              onClick={() => {
                sendHomeEvent({
                  actionName: 'popup_landing',
                  action: 'click',
                  page: 'main',
                  customProps: {
                    popup_type: 'front',
                    event_layer: 'popup',
                    popup_id: eventPopup.id,
                  },
                });
                onRequestClose();
                routeTo(eventPopup.landingUri);
              }}
              isEllipsisText
              isActiveTextOpacity50
            >
              {eventPopup.buttonTitle}
            </TextButton>
          </div>
          <Anchor
            onClick={() => {
              sendHomeEvent({
                actionName: 'popup_never_show',
                action: 'click',
                page: 'main',
                customProps: {
                  popup_type: 'front',
                  event_layer: 'popup',
                  popup_id: eventPopup.id,
                },
              });
              onClose(true);
            }}
            className="fixed inset-x-0 m-auto bottom-30 z-1"
          >
            <Text className="underline s14-regular-grey07">
              {t('mainhome_popup_dontshow_button')}
            </Text>
          </Anchor>
        </div>
      )}
    </Modal>
  );
}
